import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays,
  ClipboardList,
  CookingPot,
  FileText,
  HeartPulse,
  LayoutGrid,
  type LucideIcon,
  Salad,
  Sparkles,
  Target,
  Utensils,
  UtensilsCrossed,
} from 'lucide-react';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { cn } from '@/lib/utils';

/**
 * Horizontal icon + label sub-nav for the patient route. Replaces the old
 * text-only tab bar with the reference dashboard's button-strip look while
 * keeping every existing route + active-tab state untouched.
 *
 * Each tab carries its own icon — mostly self-explanatory matches (Utensils
 * for Logs, Target for Goals, etc.); see the icon table below if you want to
 * swap one. Icons come from lucide-react which is already in v2.
 */
export type PatientTab = {
  to: string;
  label: string;
  emotionsOverride?: string;
};

/** Icon lookup keyed on `tab.to`. Falls back to Sparkles for unknowns so
 *  adding a new tab doesn't break the nav until you wire its icon. */
const TAB_ICONS: Record<string, LucideIcon> = {
  summary: LayoutGrid,
  logs: Utensils,
  'logs-v2': UtensilsCrossed, // Meals
  'logs-v3': Utensils,
  nutrients: Salad,
  prescriptions: Target,
  recipes: CookingPot,
  'meal-plans': ClipboardList,
  emotions: HeartPulse,
  reports: FileText,
  subscriptions: CalendarDays,
};

type Props = {
  patientId: number;
  tabs: PatientTab[];
  /** Optional override label for the Emotions tab — driven by clinic config
   *  (some clinics swap to "Digestion"). When provided, takes precedence
   *  over `label` for the `emotions` route only. */
  emotionsLabel?: string;
};

export const PatientTabNav = ({ patientId, tabs, emotionsLabel }: Props) => {
  const { t } = useTranslation();
  return (
    <nav
      role="tablist"
      aria-label={t('Patient sections')}
      className="flex flex-wrap gap-1 rounded-md border bg-card p-1 shadow-sm"
    >
      {tabs.map((tab) => {
        const Icon = TAB_ICONS[tab.to] ?? Sparkles;
        const label =
          tab.to === 'emotions' && emotionsLabel ? emotionsLabel : tab.label;
        // Drop `end` for nested-routing tabs so deep paths under the tab
        // (e.g. `/meal-plans/new`) still highlight the tab as active.
        const isNested = tab.to === 'meal-plans';
        return (
          <NavLink
            key={tab.to}
            to={`/patients/${patientId}/${tab.to}`}
            role="tab"
            end={!isNested}
            onClick={() => track(ANALYTICS_EVENTS.TAB_CHANGED, { tab: tab.to })}
            className={({ isActive }) =>
              cn(
                'flex flex-1 min-w-[72px] flex-col items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[0.6875rem] font-medium transition-colors',
                // Active = solid teal (like the Add-patient button); hover =
                // dark teal + white text. Icon + label inherit this text color.
                isActive
                  ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                  : 'text-muted-foreground hover:bg-primary-hover hover:text-primary-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            <span className="truncate">{t(label)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

/**
 * Re-export the icon map so the LeftRail or other surfaces can reuse the
 * same icon-per-tab association (e.g. for a "Jump to Goals" affordance).
 */
export { TAB_ICONS };
